import { Position, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import { useState, useEffect } from 'react';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import './style.scss';
import { UilTrashAlt, UilPlus } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { useEdges } from '@xyflow/react';
import { Button } from 'react-bootstrap';

const emptyRoute = () => ({ routingPrompt: '', phoneNumber: '' });

export default function CustomCallConnectNode({ id, data, editable = true }) {
  const { setNodes } = useReactFlow();
  const trashNode = useTrashNode();
  const edges = useEdges();

  const DEFAULT_TRANSITIONS = [
    { id: `transition-${id}-0`, value: 'Success' },
    { id: `transition-${id}-1`, value: 'Failure' },
    { id: `transition-${id}-2`, value: 'Callback' }
  ];

  const ensureTransitions = list => {
    if (!Array.isArray(list) || list.length === 0) return DEFAULT_TRANSITIONS;
    if (list.length >= 3) return list;
    const next = [...list];
    if (!next.some(t => /callback|call\s*later|later/i.test(t?.value || ''))) {
      next.push({
        id: `transition-${id}-${next.length}`,
        value: 'Callback'
      });
    }
    return next;
  };

  const [items, setItems] = useState(() =>
    ensureTransitions(data?.transitions)
  );

  const [routes, setRoutes] = useState(
    () =>
      Array.isArray(data?.callConnectNumberRoutes) &&
      data.callConnectNumberRoutes.length > 0
        ? data.callConnectNumberRoutes.map(r => ({
            routingPrompt: r?.routingPrompt ?? '',
            phoneNumber: r?.phoneNumber ?? ''
          }))
        : []
  );

  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    updateNodeInternals(id);
  }, []);

  useEffect(() => {
    if (Array.isArray(data?.callConnectNumberRoutes)) {
      setRoutes(
        data.callConnectNumberRoutes.map(r => ({
          routingPrompt: r?.routingPrompt ?? '',
          phoneNumber: r?.phoneNumber ?? ''
        }))
      );
    }
  }, [data?.callConnectNumberRoutes]);

  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  const persistRoutes = nextRoutes => {
    setRoutes(nextRoutes);
    onChange({ callConnectNumberRoutes: nextRoutes });
  };

  const addRoute = () => {
    persistRoutes([...routes, emptyRoute()]);
  };

  const removeRoute = index => {
    const next = routes.filter((_, i) => i !== index);
    persistRoutes(next);
  };

  const updateRoute = (index, field, value) => {
    const next = routes.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    persistRoutes(next);
  };

  const IsValidConnection = connection => {
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;
    return true;
  };

  useEffect(() => {
    const next = ensureTransitions(data?.transitions);
    if (
      !data?.transitions ||
      data.transitions.length === 0 ||
      data.transitions.length < 3
    ) {
      setItems(next);
      onChange({ transitions: next });
      updateNodeInternals(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <dl className="mt-3" style={{ width: '320px' }}>
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div
          className="d-flex align-items-center px-4 node-header"
          style={{ backgroundColor: '#F4E5FF' }}
        >
          <div className="d-flex mr-auto ml-2">
            <dt
              className="text-truncate fw-semibold fs-6"
              style={{ color: '#68219A' }}
            >
              Call Connect Node
            </dt>
          </div>
          <div className="ms-auto">
            <Button
              variant="transparent"
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 header-delete-icon"
              title="Delete call_connect"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
            </Button>
          </div>
        </div>

        <div className="card-body p-2">
          <div className="px-2 pb-2 small text-secondary">
            Optional: add routes (when to dial + number). If you add none, the
            chatbot’s existing Call Connect number is used.
          </div>
          {routes.map((row, index) => (
            <div
              key={index}
              className="px-2 mb-2 p-2 rounded border"
              style={{ backgroundColor: '#fff', borderColor: '#e9ecef' }}
            >
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span
                  className="small fw-medium"
                  style={{ color: '#6B7280' }}
                >
                  Route {index + 1}
                </span>
                <Button
                  variant="link"
                  className="p-0 text-danger small"
                  size="sm"
                  disabled={!editable}
                  onClick={() => removeRoute(index)}
                >
                  Remove
                </Button>
              </div>
              <div className="mb-1">
                <div className="small text-muted mb-1">When to use (prompt)</div>
                <textarea
                  className="form-control form-control-sm"
                  rows={2}
                  placeholder="Describe when to dial this number…"
                  value={row.routingPrompt}
                  disabled={!editable}
                  onChange={e =>
                    updateRoute(index, 'routingPrompt', e.target.value)
                  }
                />
              </div>
              <div>
                <div className="small text-muted mb-1">Number</div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Phone number"
                  value={row.phoneNumber}
                  disabled={!editable}
                  onChange={e =>
                    updateRoute(index, 'phoneNumber', e.target.value)
                  }
                />
              </div>
            </div>
          ))}
          <div className="px-2 mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              className="w-100 d-flex align-items-center justify-content-center gap-1"
              disabled={!editable}
              onClick={addRoute}
            >
              <UilPlus size={16} />
              Add number route
            </Button>
          </div>

          <div className="px-2 pb-3">
            <dt
              className="text-truncate small fw-medium mt-1 px-2"
              style={{ color: '#6B7280', fontSize: '16px' }}
            >
              Transitions
            </dt>
            {items.map(item => (
              <div
                key={item.id}
                className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
              >
                <FlowHandle
                  type="source"
                  position={Position.Right}
                  id={item.id}
                  style={{
                    right: '-13px',
                    top: '50%'
                  }}
                  editable={false}
                  isConnectable={true}
                  isValidConnection={IsValidConnection}
                />
                <SimpleLabelTextBox
                  dataProperty={item.value}
                  editable={false}
                  inputStyles={{
                    backgroundColor: '#f1f3f5',
                    cursor: 'not-allowed'
                  }}
                  inputPropStyle={{ padding: '10px' }}
                  styles={{ flex: 1 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </dl>
  );
}
